// The routes of the custom lobby on the meta server (deployment.md §6.7,
// R3088, ui-map-interface.md §16.17).
//
// A Premium player hosts a private custom world from the EVENT tile of the
// hub. The lobby is a row of the meta server, and the page polls its view:
// `GET /api/custom/lobbies/{code}?rev=N` answers 304 while the revision
// stands, so a poll of an unchanged lobby costs no body. Every act answers
// the whole new view, so the panel mirrors no rule of a lobby: it draws
// the view it was last given. A refusal travels in the one error shape of
// the meta server, and its message is already in the reader's language.

import { apiCall } from './api';

/** The six states of a lobby (R3088, R3110). The last three are final:
 *  `ended` is a started lobby whose run is final. */
export type LobbyState = 'draft' | 'open' | 'started' | 'ended' | 'cancelled' | 'dissolved';

/** The final state of the run of an ended lobby (R3110). */
export type LobbyEndReason = 'released' | 'failed' | 'cancelled';

/** One instant the schedule names, in unix seconds. */
export interface LobbyRun {
  label: string;
  opensAt: number;
  closesAt?: number;
}

/** What `GET /api/custom/host` answers: may this account host now? */
export interface HostState {
  canHost: boolean;
  /** Why not, in the reader's language. Absent when `canHost`. */
  reason?: { code: string; message: string };
  /** The unix second the hosting budget renews, while it is spent. */
  budgetUntil?: number;
  /** The next scheduled run, which a lobby must end before. */
  nextRun?: LobbyRun;
  /** The code of the caller's own live lobby, as host or as member, if
   *  one stands. */
  lobby?: string;
  /** The state of that lobby; '' or absent with no live lobby. An older
   *  server omits every field below, and the client then reads a lobby
   *  that is not started. */
  lobbyState?: '' | 'draft' | 'open' | 'started';
  /** The world of a started lobby, 0 until the run names its world. */
  worldId?: number;
  /** True when the world is up and its open time passed, so a join
   *  passes. Absent reads as false. */
  worldOpen?: boolean;
  /** The open and the close of the run of a started lobby, in unix
   *  seconds, else 0. */
  opensAt?: number;
  closesAt?: number;
  /** The world name of a started lobby, else ''. */
  label?: string;
}

/** One member of a lobby. `id` is the public id of the account (a UUID);
 *  `faction` and `squad` are 0 for "auto". */
export interface LobbyMember {
  id: string;
  name: string;
  faction: number;
  squad: number;
  host: boolean;
}

/** The settings of a lobby (R3090, R3093, R3094). */
export interface LobbySettings {
  name: string;
  seats: number;
  /** FILL WITH BOTS (R3094): population is then a target. */
  fill: boolean;
  population: number;
  /** The enabled factions, each in 1..5. */
  factions: number[];
  timescale: number;
  tacticalScale: number;
  combatPace: number;
  seed: number;
  /** The server-scope flags that stay on (R3093). */
  flags: string[];
  seasonDays: number;
  truceSec: number;
  stage1Frac: number;
  stage2Frac: number;
}

/** A gate of one act: OPEN or START. */
export interface LobbyGate {
  ok: boolean;
  code?: string;
  message?: string;
}

/** The whole view of one lobby, as every route answers it. */
export interface LobbyView {
  rev: number;
  code: string;
  link: string;
  state: LobbyState;
  host: { id: string; name: string };
  /** The public id of the reader's account. */
  you: string;
  isHost: boolean;
  members: LobbyMember[];
  settings: LobbySettings;
  /** The setting keys that no longer change (R3090). */
  locked: string[];
  /** The server-scope flags of the environment (R3093). */
  envFlags: string[];
  /** The commanders of each faction, humans and bots, by faction id. */
  commanders: Record<string, number>;
  /** The wall length of the world, in seconds. */
  wallSec: number;
  expiresAt?: number;
  autostartAt?: number;
  opensAt?: number;
  /** The close of the run of a started lobby, in unix seconds. */
  closesAt?: number;
  /** True when the world of a started lobby is up and its open time
   *  passed, so a join passes. Absent reads as false: the page lets
   *  nobody in before the open. */
  worldOpen?: boolean;
  nextRun?: LobbyRun;
  canOpen: LobbyGate;
  canStart: LobbyGate;
  /** The world of the run, after START. */
  worldId?: number;
  /** Why an ended lobby ended: the final state of its run. */
  endReason?: LobbyEndReason;
  /** True when the hosting budget came back (a failed run, R3092). */
  refunded?: boolean;
}

/** A change of the settings: any subset of the keys. */
export type SettingsPatch = Partial<LobbySettings>;

/** A change of one member: a faction, a squad, or KICK. */
export interface MemberPatch {
  faction?: number;
  squad?: number;
  kick?: true;
}

const BASE = '/api/custom';

function lobbyPath(code: string, tail = ''): string {
  return `${BASE}/lobbies/${encodeURIComponent(code)}${tail}`;
}

/** May this account host now, and what stands in its way. */
export function getHostState(): Promise<HostState> {
  return apiCall<HostState>('GET', `${BASE}/host`);
}

/** HOST: a new draft lobby with the caller as its host. */
export function createLobby(): Promise<LobbyView> {
  return apiCall<LobbyView>('POST', `${BASE}/lobbies`, {});
}

/**
 * The view of one lobby, or null when the revision `rev` still stands
 * (304). No `rev` asks for the view whatever it is. A revision is a token
 * the page compares for equality alone: the server counts it in steps of
 * its own.
 */
export function getLobby(code: string, rev?: number): Promise<LobbyView | null> {
  const q = rev === undefined ? '' : `?rev=${rev}`;
  return apiCall<LobbyView | null>('GET', lobbyPath(code, q), undefined, { notModified: true });
}

/** Joins a lobby by its code. */
export function joinLobby(code: string): Promise<LobbyView> {
  return apiCall<LobbyView>('POST', lobbyPath(code, '/join'), {});
}

/** Leaves a lobby. The host of an OPEN lobby is refused: CANCEL ends it. */
export function leaveLobby(code: string): Promise<{ ok: boolean }> {
  return apiCall<{ ok: boolean }>('POST', lobbyPath(code, '/leave'), {});
}

/**
 * Changes some settings (the host alone). The body is the patch itself:
 * the server refuses an unknown key, so a wrapped body answers 400.
 */
export function putLobbySettings(code: string, patch: SettingsPatch): Promise<LobbyView> {
  return apiCall<LobbyView>('PUT', lobbyPath(code, '/settings'), patch);
}

/** Moves or kicks one member (the host alone). */
export function putLobbyMember(code: string, id: string, patch: MemberPatch): Promise<LobbyView> {
  return apiCall<LobbyView>('PUT', lobbyPath(code, `/members/${encodeURIComponent(id)}`), patch);
}

/** The three acts of the host. */
export type LobbyAct = 'open' | 'cancel' | 'start';

/** OPEN, CANCEL or START (the host alone). */
export function lobbyAct(code: string, act: LobbyAct): Promise<LobbyView> {
  return apiCall<LobbyView>('POST', lobbyPath(code, `/${act}`), {});
}
